// src/features/RADLayout/hooks/useRadDiagram.js
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import * as d3 from "d3";
import { createSvg, drawRADLayout, buildRADLayout } from "@cadenza/rad-render";

import {
  getRoleThreads,
  getThreadSequence,
  getThreadCaseRefinements,
  getThreadPartRefinements,
  getThreadPartRepeats,
  getSubthreads,
  addPrecedingPositions,
  getFarY,
  getConnectedInteractions,
  getActivityByPrestate,
  getActivityByPoststate,
  getRADName,
} from "@cadenza/rad-domain";

import { useZoom, useDragBehavior } from "../hooks/dragZoomBehaviour.jsx";
import { useDropLogic } from "../hooks/dropBehaviour.jsx";
import { useDrop } from "../../RADHandler/contexts/DropContext.jsx";

/**
 * Encapsulates:
 *  - ref for the container that holds the SVG
 *  - createSvg + drawRADLayout lifecycle
 *  - zoom behavior
 *  - drag behavior (can be disabled)
 *  - highlighting + connecting UI
 *  - drop-to-add logic
 */
export const useRadDiagram = ({
  rad,
  onBackgroundClick,
  onClick,
  handleMove,
  selectedComponent,
  isConnecting,
  isLooping,
  isMerging,
  showTargets,
  viewBoxWidth = 750,
  viewBoxHeight = 750,
  minScale = 0.1,
  maxScale = 3,
  dragEnabled = true,
}) => {
  const containerRef = useRef(null);
  const [computedRAD, setComputedRAD] = useState(null);

  // zoom
  const {
    zoomIn,
    zoomOut,
    resetZoom,
    zoomToFit,
    zoomBehavior,
    setScale,
    currentScale,
  } = useZoom(containerRef, viewBoxWidth, viewBoxHeight, minScale, maxScale);

  // drag
  const { dragBehavior } = useDragBehavior(handleMove);
  // Renderer expects a *factory* it can call: dragBehavior() -> (selection)=>selection.call(...)
  // So our no-op must also be a factory returning a callable.
  const noopDragFactory = useCallback(() => (sel) => sel, []);
  const effectiveDrag = useMemo(
    () => (dragEnabled ? dragBehavior : noopDragFactory),
    [dragEnabled, dragBehavior, noopDragFactory]
  );

  // drop-to-add
  const { draggedShape, setDraggedShape } = useDrop();
  const { addDroppedItem } = useDropLogic(
    containerRef,
    draggedShape,
    viewBoxWidth,
    viewBoxHeight
  );

  // 1) create <svg> once + meet aspect
  useEffect(() => {
    if (!containerRef.current) return;
    createSvg(containerRef, viewBoxWidth, viewBoxHeight, onBackgroundClick);
    const svg = d3.select(containerRef.current).select("svg");
    svg.attr("preserveAspectRatio", "xMidYMid meet");
  }, [onBackgroundClick, viewBoxWidth, viewBoxHeight]);

  // 2) compute layout when rad changes
  useEffect(() => {
    if (!rad) return;
    setComputedRAD(buildRADLayout(rad));
  }, [rad]);

  // 3) draw layout + background bbox rect
  useEffect(() => {
    if (!computedRAD || !containerRef.current) return;
    drawRADLayout(
      containerRef.current,
      computedRAD,
      effectiveDrag,
      (d) => onClick?.(d, computedRAD),
      {
        getRoleThreads,
        getThreadSequence,
        getThreadCaseRefinements,
        getThreadPartRefinements,
        getThreadPartRepeats,
        getSubthreads,
        addPrecedingPositions,
        getFarY,
        getConnectedInteractions,
        getActivityByPrestate,
        getActivityByPoststate,
      }
    );

    // ensure a background rect that fits content, with padding
    // and add the rad name as title for accessibility
    const g = d3.select(containerRef.current).select("#rad-layout");
    if (!g.empty() && g.node().getBBox) {
      g.select(".rad-layout-background").remove();

      const bbox = g.node().getBBox();
      const padding = 20;
      let x = bbox.x - padding;
      let y = bbox.y - padding;
      let width = bbox.width + padding * 2;
      let height = bbox.height + padding * 2;

      if (bbox.width === 0 && bbox.height === 0) {
        x = 0;
        y = 0;
        width = viewBoxWidth;
        height = viewBoxHeight;
      }

      g.insert("rect", ":first-child")
        .attr("class", "rad-layout-background")
        .attr("x", x)
        .attr("y", y)
        .attr("width", width)
        .attr("height", height);

      // add title for accessibility
      const radName = getRADName(computedRAD);

      g.select("title").remove();
      g.insert("title", ":first-child")
        .text(radName || "RAD Diagram")
        .attr("id", "rad-diagram-title");

      // Add RAD name as visible title in viewport coordinates
      const svg = d3.select(containerRef.current).select("svg");
      svg.select(".rad-title-group").remove();

      if (radName) {
        const titleGroup = svg.append("g").attr("class", "rad-title-group");

        titleGroup
          .append("text")
          .attr("class", "rad-title")
          .attr("x", viewBoxWidth - 50)
          .attr("y", 50)
          .attr("text-anchor", "end")
          .text(radName);
      }
    }
  }, [computedRAD, effectiveDrag, onClick, viewBoxWidth, viewBoxHeight]);

  // 4) apply zoom behavior to svg
  useEffect(() => {
    if (!containerRef.current) return;
    const svg = d3.select(containerRef.current).select("svg");
    svg.call(zoomBehavior);
  }, [zoomBehavior]);

  // 5) drop-to-add
  useEffect(() => {
    if (!draggedShape) return;
    addDroppedItem(draggedShape);
    setDraggedShape(null);
  }, [draggedShape, addDroppedItem, setDraggedShape]);

  // 6) highlight selected component
  useEffect(() => {
    if (!containerRef.current) return;

    const highlight = (id) => {
      d3.select(containerRef.current)
        .selectAll("*")
        .classed("highlighted", false);
      if (!id) return;
      d3.selectAll(`[data-id='${id}']`).classed("highlighted", true);
    };

    const highlightConnection = (connection) => {
      d3.select(containerRef.current).selectAll("*").classed("accented", false);
      d3.select(containerRef.current)
        .selectAll("*")
        .classed("highlighted", false);

      if (!connection) return;

      d3.selectAll(`[data-connection-id='${connection.id}']`).classed(
        "accented",
        true
      );
      // The specific segment selected gets a bolder highlight
      /* d3.selectAll(`[data-id='${connection.segment.id}']`).classed(
        "highlighted",
        true
      ); */
    };

    // Interactions are highlighted as a group, so we need to treat them differently
    if (!selectedComponent) {
      highlight(null);
      highlightConnection(null);
    } else if (selectedComponent.class === "connection") {
      highlightConnection(selectedComponent ?? null);
    } else {
      highlight(selectedComponent?.id ?? null);
    }
  }, [selectedComponent]);

  // 7) connecting highlight
  useEffect(() => {
    if (!containerRef.current) return;

    const highlightSource = (id, className) => {
      d3.select(containerRef.current)
        .selectAll("*")
        .classed("connecting", false);
      if (!id) return;
      if (className === "connection") {
        d3.selectAll(`[data-connection-id='${id}']`).classed(
          "connecting",
          true
        );
        return;
      }
      d3.selectAll(`[data-id='${id}']`).classed("connecting", true);
    };

    const highlightTarget = (id, showVisuals = true) => {
      if (!id) {
        d3.select(containerRef.current)
          .selectAll("*")
          .classed("connectionTarget", false)
          .classed("connectionTargetHidden", false);
      } else {
        d3.selectAll(`[data-id='${id}']`)
          .classed("connectionTarget", showVisuals)
          .classed("connectionTargetHidden", !showVisuals);
      }
    };

    // Clear all connecting states first
    d3.select(containerRef.current)
      .selectAll("*")
      .classed("connectingMode", false);

    if (isConnecting?.source && !isConnecting?.targets) {
      highlightSource(isConnecting.source.id, isConnecting.source.class);
      // Add connecting mode class to the container for CSS hover rules
      d3.select(containerRef.current)
        .select("svg")
        .classed("connectingMode", true);
    } else if (isConnecting?.source && isConnecting?.targets) {
      highlightSource(isConnecting.source.id, isConnecting.source.class);
      // Always apply target classes for hover detection, but only show visuals if showTargets is true
      isConnecting.targets.forEach((id) => highlightTarget(id, showTargets));
      // Add connecting mode class to the container for CSS hover rules
      d3.select(containerRef.current)
        .select("svg")
        .classed("connectingMode", true);
    } else {
      highlightSource(null, null);
      highlightTarget(null);
    }
  }, [isConnecting, showTargets]);

  // 8) looping highlight
  useEffect(() => {
    if (!containerRef.current || !isLooping) {
      return;
    }
    const highlightLoop = (id) => {
      d3.select(containerRef.current).selectAll("*").classed("looping", false);
      if (!id) return;
      d3.selectAll(`[data-id='${id}']`).classed("looping", true);
    };

    const highlightLoopTarget = (id) => {
      if (!id) {
        d3.select(containerRef.current)
          .selectAll("*")
          .classed("loopTarget", false);
      } else {
        d3.selectAll(`[data-id='${id}']`).classed("loopTarget", true);
      }
    };
    if (isLooping.length === 1) {
      highlightLoop(isLooping[0].id);
    } else if (isLooping.length === 2) {
      highlightLoop(isLooping[0].id);
      isLooping[1].forEach((id) => highlightLoopTarget(id));
    } else {
      highlightLoop(null);
      highlightLoopTarget(null);
    }
  }, [isLooping]);

  // 9) merging highlight
  useEffect(() => {
    if (!containerRef.current || !isMerging) {
      return;
    }
    const highlightMerge = (id) => {
      d3.select(containerRef.current).selectAll("*").classed("merging", false);
      if (!id) return;
      d3.selectAll(`[data-id='${id}']`).classed("merging", true);
    };

    const highlightMergeTarget = (id) => {
      if (!id) {
        d3.select(containerRef.current)
          .selectAll("*")
          .classed("mergeTarget", false);
      } else {
        d3.selectAll(`[data-id='${id}']`).classed("mergeTarget", true);
      }
    };
    if (isMerging.length === 1) {
      highlightMerge(isMerging[0].id);
    } else if (isMerging.length === 2) {
      highlightMerge(isMerging[0].id);
      isMerging[1].forEach((id) => highlightMergeTarget(id));
    } else {
      highlightMerge(null);
      highlightMergeTarget(null);
    }
  }, [isMerging]);

  // stable zoom API object (identity only changes when contents change)
  const zoomApi = useMemo(
    () => ({
      zoomIn,
      zoomOut,
      resetZoom,
      zoomToFit,
      setScale,
      get currentScale() {
        return currentScale;
      },
      minScale,
      maxScale,
    }),
    [
      zoomIn,
      zoomOut,
      resetZoom,
      zoomToFit,
      setScale,
      currentScale,
      minScale,
      maxScale,
    ]
  );

  return { containerRef, zoomApi };
};
